/**
 * ===========================================
 * HERO SLIDER PRO - JAVASCRIPT (ACTUALIZADO)
 * ===========================================
 * ✅ Soporte para videos nativos HTML5
 * ✅ Controles en esquina superior derecha
 * ✅ Contenido limitado a 1300px
 */

class HeroSliderPro {
  constructor(element) {
    this.container = element;
    if (!this.container) return;

    // ===== ELEMENTOS DEL DOM =====
    this.slides = this.container.querySelectorAll('.hero-slider__slide');
    this.muteUnmuteBtn = this.container.querySelector('[data-action="mute-unmute"]');
    this.prevBtn = this.container.querySelector('[data-action="prev"]');
    this.nextBtn = this.container.querySelector('[data-action="next"]');

    // ===== CONFIGURACIÓN DEL SLIDER =====
    this.isAutoplayEnabled = this.container.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.container.dataset.autoplaySpeed, 10) || 7000;
    this.autoplayInterval = null;
    
    // ===== ESTADOS INICIALES =====
    this.currentSlide = 0;
    this.players = {}; // Reproductores (YouTube, Vimeo, HTML5)
    this.isMuted = true;
    this.isTransitioning = false;

    this.init();
  }

  init() {
    if (this.slides.length === 0) return;
    
    this.handleSingleSlide();
    this.updateButtonStates();
    this.initVideoPlayers();
    this.bindEvents();
    this.resizeVideoBackgrounds();
    this.addEntranceAnimations();

    if (this.isAutoplayEnabled) {
      this.startAutoplay();
    }
  }

  handleSingleSlide() {
    if (this.slides.length <= 1) {
      if (this.prevBtn) this.prevBtn.style.display = 'none';
      if (this.nextBtn) this.nextBtn.style.display = 'none';
      this.isAutoplayEnabled = false;
    }
  }

  addEntranceAnimations() {
    setTimeout(() => {
      if (this.slides[0]) {
        this.slides[0].classList.add('entrance-animation');
      }
    }, 100);
  }

  bindEvents() {
    this.prevBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.prevSlide();
      this.resetAutoplay();
    });

    this.nextBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.nextSlide();
      this.resetAutoplay();
    });

    this.muteUnmuteBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleMute();
    });
    
    // Eventos de teclado
    document.addEventListener('keydown', (e) => {
      if (document.activeElement.closest('.hero-slider') !== this.container) return;
      if (this.slides.length <= 1) return;
      
      switch(e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.prevSlide();
          this.resetAutoplay();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.nextSlide();
          this.resetAutoplay();
          break;
        case 'Home':
          e.preventDefault();
          this.goToSlide(0);
          this.resetAutoplay();
          break;
        case 'End':
          e.preventDefault();
          this.goToSlide(this.slides.length - 1);
          this.resetAutoplay();
          break;
      }
    });
    
    // Resize
    window.addEventListener('resize', () => {
      this.resizeVideoBackgrounds();
    });
    
    // Visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoplay();
        this.pauseAllMedia();
      } else if (this.isAutoplayEnabled) {
        this.startAutoplay();
        this.playMedia(this.currentSlide);
      }
    });

    this.addTouchEvents();
  }

  addTouchEvents() {
    let startX = 0;
    let startY = 0;
    const minSwipeDistance = 50;

    this.container.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    this.container.addEventListener('touchend', (e) => {
      if (this.slides.length <= 1) return;
      
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
        if (deltaX > 0) {
          this.prevSlide();
        } else {
          this.nextSlide();
        }
        this.resetAutoplay();
      }
    }, { passive: true });
  }

  initVideoPlayers() {
    this.slides.forEach((slide, index) => {
      const youtubeEl = slide.querySelector('[data-youtube-id]');
      const vimeoEl = slide.querySelector('[data-vimeo-id]');
      const nativeVideo = slide.querySelector('[data-native-video]');

      if (nativeVideo) {
        this.initNativeVideo(nativeVideo, index);
      } else if (youtubeEl && typeof YT !== 'undefined' && YT.Player) {
        this.initYouTubePlayer(youtubeEl, index);
      } else if (vimeoEl && typeof Vimeo !== 'undefined' && Vimeo.Player) {
        this.initVimeoPlayer(vimeoEl, index);
      }
    });
  }

  // ===== NUEVO: Soporte para videos nativos HTML5 =====
  initNativeVideo(videoElement, index) {
    this.players[index] = videoElement;
    
    videoElement.muted = true;
    videoElement.volume = 0;
    
    // Intentar reproducir cuando el video esté listo
    const tryPlay = () => {
      videoElement.play().catch(err => {
        console.warn(`Autoplay bloqueado en video nativo ${index}:`, err);
        // Intentar de nuevo después de interacción del usuario
        document.addEventListener('click', () => {
          videoElement.play().catch(e => console.warn('No se pudo reproducir:', e));
        }, { once: true });
      });
    };

    videoElement.addEventListener('loadeddata', () => {
      if (index === 0) {
        setTimeout(tryPlay, 800);
      }
    });

    videoElement.addEventListener('error', (e) => {
      console.error(`Error cargando video nativo ${index}:`, e);
    });

    // Si el video ya está cargado, reproducir inmediatamente
    if (videoElement.readyState >= 3 && index === 0) {
      setTimeout(tryPlay, 800);
    }
  }

  initYouTubePlayer(element, index) {
    const videoId = element.dataset.youtubeId;
    if (!videoId) return;

    this.players[index] = new YT.Player(element, {
      videoId: videoId,
      playerVars: {
        autoplay: 0, controls: 0, rel: 0, showinfo: 0, iv_load_policy: 3,
        modestbranding: 1, loop: 1, playlist: videoId, playsinline: 1, mute: 1
      },
      events: {
        'onReady': (event) => {
          event.target.setVolume(this.isMuted ? 0 : 100);
          if (index === 0) {
            setTimeout(() => {
              event.target.playVideo();
            }, 500);
          }
        },
        'onError': (event) => console.error(`Error en YouTube player ${index}:`, event.data)
      }
    });
  }

  initVimeoPlayer(element, index) {
    this.players[index] = new Vimeo.Player(element);
    this.players[index].ready().then(() => {
      this.players[index].setVolume(this.isMuted ? 0 : 1);
      this.players[index].setLoop(true);
      if (index === 0) {
        setTimeout(() => {
          this.players[index].play();
        }, 500);
      }
    }).catch(error => console.error(`Error inicializando Vimeo player ${index}:`, error));
  }

  resizeVideoBackgrounds() {
    const mediaElements = this.container.querySelectorAll('.hero-slider__media-background iframe, .hero-slider__image, .hero-slider__native-video');
    mediaElements.forEach(media => {
      const container = media.closest('.hero-slider__media-background');
      if (!container) return;

      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      const videoRatio = 16 / 9;

      let newWidth, newHeight;
      if (containerWidth / containerHeight > videoRatio) {
        newWidth = containerWidth;
        newHeight = containerWidth / videoRatio;
      } else {
        newHeight = containerHeight;
        newWidth = containerHeight * videoRatio;
      }
      media.style.width = `${newWidth}px`;
      media.style.height = `${newHeight}px`;
    });
  }

  goToSlide(index) {
    if (index === this.currentSlide || this.slides.length === 0 || this.isTransitioning) return;
    
    this.isTransitioning = true;
    
    this.pauseMedia(this.currentSlide);
    this.slides[this.currentSlide].classList.remove('is-active');
    this.currentSlide = (index + this.slides.length) % this.slides.length;
    this.slides[this.currentSlide].classList.add('is-active');

    setTimeout(() => {
      this.playMedia(this.currentSlide);
      this.setMuteStateForPlayer(this.currentSlide);
    }, 300);

    setTimeout(() => { 
      this.isTransitioning = false; 
    }, 1200);
  }

  nextSlide() { 
    this.goToSlide(this.currentSlide + 1); 
  }
  
  prevSlide() { 
    this.goToSlide(this.currentSlide - 1); 
  }

  startAutoplay() {
    if (this.slides.length <= 1 || this.autoplayInterval) return;
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }
  
  resetAutoplay() {
    this.stopAutoplay();
    if (this.isAutoplayEnabled) {
      setTimeout(() => {
        this.startAutoplay();
      }, 1000);
    }
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    this.updateButtonStates();
    
    Object.values(this.players).forEach(player => {
      if (!player) return;
      
      // Video nativo HTML5
      if (player.tagName === 'VIDEO') {
        player.muted = this.isMuted;
        player.volume = this.isMuted ? 0 : 1;
      }
      // YouTube o Vimeo
      else if (typeof player.setVolume === 'function') {
        const volume = this.isMuted ? 0 : (player.hasOwnProperty('playVideo') ? 100 : 1);
        player.setVolume(volume);
      }
    });
  }

  updateButtonStates() {
    this.container.dataset.isMuted = this.isMuted;
  }
  
  playMedia(index) {
    const player = this.players[index];
    if (!player) return;

    try {
      // Video nativo HTML5
      if (player.tagName === 'VIDEO') {
        player.play().catch(err => {
          console.warn(`Error reproduciendo video nativo ${index}:`, err);
        });
      }
      // YouTube
      else if (player.hasOwnProperty('playVideo')) {
        player.playVideo();
      }
      // Vimeo
      else if (player.hasOwnProperty('play')) {
        player.play();
      }
    } catch (error) {
      console.warn(`Error reproduciendo media en slide ${index}:`, error);
    }
  }
  
  pauseMedia(index) {
    const player = this.players[index];
    if (!player) return;

    try {
      // Video nativo HTML5
      if (player.tagName === 'VIDEO') {
        player.pause();
      }
      // YouTube
      else if (player.hasOwnProperty('pauseVideo')) {
        player.pauseVideo();
      }
      // Vimeo
      else if (player.hasOwnProperty('pause')) {
        player.pause();
      }
    } catch (error) {
      console.warn(`Error pausando media en slide ${index}:`, error);
    }
  }

  pauseAllMedia() {
    Object.keys(this.players).forEach(index => {
      this.pauseMedia(parseInt(index));
    });
  }
  
  setMuteStateForPlayer(index) {
    const player = this.players[index];
    if (!player) return;

    try {
      // Video nativo HTML5
      if (player.tagName === 'VIDEO') {
        player.muted = this.isMuted;
        player.volume = this.isMuted ? 0 : 1;
      }
      // YouTube o Vimeo
      else {
        const volume = this.isMuted ? 0 : (player.hasOwnProperty('playVideo') ? 100 : 1);
        player.setVolume(volume);
      }
    } catch (error) {
      console.warn(`Error configurando volumen en slide ${index}:`, error);
    }
  }

  destroy() {
    this.stopAutoplay();
    this.pauseAllMedia();
    
    this.prevBtn?.removeEventListener('click', this.prevSlide);
    this.nextBtn?.removeEventListener('click', this.nextSlide);
    this.muteUnmuteBtn?.removeEventListener('click', this.toggleMute);
  }
}

// Inicializar todos los sliders
document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('.hero-slider');
  window.heroSliders = [];
  
  sliders.forEach(slider => {
    const sliderInstance = new HeroSliderPro(slider);
    window.heroSliders.push(sliderInstance);
  });
});

// Soporte para Shopify Theme Editor
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  const heroSlider = section.querySelector('.hero-slider');
  
  if (heroSlider) {
    setTimeout(() => {
      const sliderInstance = new HeroSliderPro(heroSlider);
      if (!window.heroSliders) window.heroSliders = [];
      window.heroSliders.push(sliderInstance);
    }, 100);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target;
  const heroSlider = section.querySelector('.hero-slider');
  
  if (heroSlider && window.heroSliders) {
    const index = window.heroSliders.findIndex(slider => 
      slider.container === heroSlider
    );
    
    if (index !== -1) {
      window.heroSliders[index].destroy();
      window.heroSliders.splice(index, 1);
    }
  }
});